import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';
import { useNavigate } from 'react-router-dom';

const CommandPalette = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const { globalSearch } = useCRM();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const results = globalSearch ? globalSearch(query) : [];

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      setSelectedIndex(prev => Math.min(results.length - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex(prev => Math.max(0, prev - 1));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelect = (item) => {
    navigate(item.path);
    onClose();
  };

  const getIcon = (type) => {
    switch (type) {
      case 'contact': return '👤';
      case 'company': return '🏢';
      case 'deal': return '💰';
      default: return '🔍';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          <motion.div initial={{ opacity: 0, scale: 0.95, y: -20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: -20 }} className="w-full max-w-2xl bg-white rounded-[2rem] shadow-2xl overflow-hidden relative z-10 border border-slate-100">
            <div className="p-6 border-b border-slate-100 flex items-center gap-4">
              <SafeIcon icon="🔍" className="w-6 h-6 text-slate-400" />
              <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder="Søk etter alt (kontakter, firmaer, avtaler...)" className="flex-1 bg-transparent border-none outline-none text-xl font-medium text-slate-900 placeholder:text-slate-300" />
              <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-[10px] font-black text-slate-400 uppercase tracking-widest">
                ESC
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto p-4">
              {query.length === 0 ? (
                <div className="py-12 text-center space-y-4">
                  <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto text-slate-300">
                    <SafeIcon icon="⌘" className="w-8 h-8" />
                  </div>
                  <p className="text-slate-400 font-medium">Begynn å skrive for å søke i hele CRM-et</p>
                </div>
              ) : results.length === 0 ? (
                <div className="py-12 text-center text-slate-400 font-medium">
                  Ingen resultater for "{query}"
                </div>
              ) : (
                <div className="space-y-1">
                  {results.map((item, index) => (
                    <div key={item.id} onClick={() => handleSelect(item)} onMouseEnter={() => setSelectedIndex(index)} className={`flex items-center justify-between p-4 rounded-2xl cursor-pointer transition-all ${index === selectedIndex ? 'bg-primary-50 text-primary-900' : 'hover:bg-slate-50'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${index === selectedIndex ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                          <SafeIcon icon={getIcon(item.type)} className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={`font-bold ${index === selectedIndex ? 'text-primary-900' : 'text-slate-900'}`}>
                            {item.title}
                          </p>
                          <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                            {item.sub}
                          </p>
                        </div>
                      </div>
                      <div className={`opacity-0 transition-opacity ${index === selectedIndex ? 'opacity-100' : ''}`}>
                        <SafeIcon icon="➡️" className="w-5 h-5 text-primary-600" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-between items-center">
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <span className="px-1.5 py-0.5 bg-white border border-slate-200 rounded">↑↓</span> Naviger
                </div>
                <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <span className="px-1.5 py-0.5 bg-white border border-slate-200 rounded">ENTER</span> Velg
                </div>
              </div>
              <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Sales Work CRM v2.0</p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;